'use client';

import {
  Badge,
  Box,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { MdMessage } from 'react-icons/md';
import { useUser } from '~/hooks/useUser';
import { isBrokerOrAdmin, isAdmin } from '~/lib/roles';

import { SearchTab } from '@habity/types';
import { SendSMSModal } from '~/components/brokers/admin/SendSMSModal';
import { SendMessageModal } from '~/components/buyers/admin/SendMessageModal';
import { useSpeculatorSelectionStore } from '~/store/useBuyerMatchSelectionStore';

interface SendMessageButtonProps {
  tab: SearchTab;
  queryParams: string;
  recipientCount: number;
  // Optional pre-loaded IDs
  brokerProviderIds?: string[];
  buyerIds?: number[];
  homeId?: number; // Optional: home ID to create matched_home interest records
  // Styling
  size?: string;
  color?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
}

const TAB_CONFIG = {
  brokers: {
    tooltip: 'Skicka SMS till alla mäklare',
    ariaLabel: 'Skicka SMS till mäklare',
  },
  speculators: {
    tooltip: 'Skicka meddelande till spekulanter',
    ariaLabel: 'Skicka meddelande till spekulanter',
  },
} as const;

export function SendMessageButton({
  tab,
  queryParams,
  recipientCount,
  brokerProviderIds,
  buyerIds,
  homeId,
  size = 'lg',
  color = 'blackAlpha.600',
  tooltipPlacement = 'bottom',
}: SendMessageButtonProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { user } = useUser();
  const { speculatorsSelection: buyersSelectionToMatch } =
    useSpeculatorSelectionStore();

  // For speculators tab, show for brokers and admins
  // For brokers tab, show only for admins
  const canAccessSpeculators = tab === 'speculators' && isBrokerOrAdmin(user);
  const canAccessBrokers = tab === 'brokers' && isAdmin(user);

  if (!canAccessSpeculators && !canAccessBrokers) {
    return null;
  }

  const config = TAB_CONFIG[tab as keyof typeof TAB_CONFIG];

  // For speculators, use selected buyers if any, otherwise use all
  const selectedBuyerCount = buyersSelectionToMatch.length;
  const selectedBuyerIds = buyersSelectionToMatch.map((b) => b.id);
  const effectiveBuyerIds =
    tab === 'speculators' && selectedBuyerCount > 0
      ? selectedBuyerIds
      : buyerIds;
  const effectiveBuyerCount =
    tab === 'speculators' && selectedBuyerCount > 0
      ? selectedBuyerCount
      : recipientCount;

  return (
    <>
      <Tooltip label={config.tooltip} placement={tooltipPlacement}>
        <Box position="relative" display="inline-block">
          <IconButton
            aria-label={config.ariaLabel}
            icon={<MdMessage />}
            onClick={onOpen}
            variant="secondary"
            flexShrink="0"
            size={size}
            color={color}
            _hover={{ color: 'black', bg: 'blackAlpha.50' }}
            _focus={{ color: 'blackAlpha.600' }}
          />
          {tab === 'speculators' && (
            <Badge
              position="absolute"
              top="-1"
              right="-1"
              colorScheme={selectedBuyerCount > 0 ? 'green' : 'gray'}
              borderRadius="full"
              fontSize="xs"
              minW="5"
              textAlign="center"
            >
              {effectiveBuyerCount}
            </Badge>
          )}
        </Box>
      </Tooltip>

      {tab === 'brokers' && (
        <SendSMSModal
          isOpen={isOpen}
          onClose={onClose}
          queryParams={queryParams}
          brokerCount={recipientCount}
          brokerProviderIds={brokerProviderIds}
        />
      )}

      {tab === 'speculators' && (
        <SendMessageModal
          isOpen={isOpen}
          onClose={onClose}
          queryParams={queryParams}
          buyerCount={effectiveBuyerCount}
          buyerIds={effectiveBuyerIds}
          homeId={homeId}
        />
      )}
    </>
  );
}
